import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { AlertTriangle, Download, Plus, Trash2, Calendar as CalendarIcon, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { adminApi } from '../../utils/admin-api';
import { generateCustomPLCReport } from '../../utils/advanced-pdf-generator';

interface ScrapManagementProps {
  clientId: string;
  token: string;
  permissions: any;
}

export function ScrapManagement({ clientId, token, permissions }: ScrapManagementProps) {
  const [entries, setEntries] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [startDate, setStartDate] = useState<Date>();
  const [endDate, setEndDate] = useState<Date>();
  const [ratePerKgInput, setRatePerKgInput] = useState(() =>
    localStorage.getItem(`scrap_rate_${clientId}`) || ''
  );

  const [formData, setFormData] = useState({
    machine: '',
    partNumber: '',
    productionKg: '',
    scrapKg: '',
    reason: 'Production Scrap',
    operator: '',
    notes: '',
  });

  useEffect(() => { loadEntries(); }, [startDate, endDate]);

  const loadEntries = async () => {
    try {
      setLoading(true);
      const options: any = { limit: 500 };
      if (startDate) options.startDate = format(startDate, 'yyyy-MM-dd');
      if (endDate) options.endDate = format(endDate, 'yyyy-MM-dd');
      const response = await adminApi.getScrapData(token, clientId, options);
      setEntries(response.data || []);
    } catch (error: any) {
      toast.error(error.message || 'Failed to load scrap entries');
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  const totalProduction = useMemo(() =>
    entries.reduce((sum, e) => sum + (Number(e.quantity) || 0), 0), [entries]);
  const totalScrap = totalProduction;
  const ratePerKg = Number(ratePerKgInput) > 0 ? Number(ratePerKgInput) : 0;
  const totalValue = totalScrap * ratePerKg;

  const handleSaveRate = () => {
    localStorage.setItem(`scrap_rate_${clientId}`, ratePerKgInput);
    toast.success('Rate saved');
  };

  const handleAddEntry = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!permissions?.scrapManagement?.edit) {
      toast.error('You do not have permission to add entries');
      return;
    }
    const scrapKg = Number(formData.scrapKg);
    if (!formData.machine) { toast.error('Machine name is required'); return; }
    if (!Number.isFinite(scrapKg) || scrapKg < 0) { toast.error('Enter valid scrap quantity'); return; }

    try {
      setSaving(true);
      await adminApi.createScrapData(token, clientId, {
        entryDate: new Date().toISOString().split('T')[0],
        machine: formData.machine,
        partNumber: formData.partNumber || 'N/A',
        quantity: scrapKg,
        reason: formData.reason || 'Production Scrap',
        description: formData.notes || null,
        operator: formData.operator || null,
      });

      await adminApi.logClientAuditEvent(token, clientId, {
        eventType: 'scrap_entry_created',
        description: 'Created a scrap entry',
        metadata: {
          machine: formData.machine,
          partNumber: formData.partNumber || 'N/A',
          quantity: scrapKg,
          reason: formData.reason || 'Production Scrap',
        },
      }).catch((error) => {
        console.warn('Failed to record scrap creation audit event:', error);
      });

      toast.success('Scrap entry saved to database');
      setFormData({ machine: '', partNumber: '', productionKg: '', scrapKg: '', reason: 'Production Scrap', operator: '', notes: '' });
      loadEntries();
    } catch (error: any) {
      toast.error(error.message || 'Failed to save entry');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!permissions?.scrapManagement?.edit) {
      toast.error('You do not have permission to delete entries');
      return;
    }
    if (!confirm('Delete this scrap entry?')) return;
    try {
      await adminApi.deleteScrapData(token, clientId, String(id));
      await adminApi.logClientAuditEvent(token, clientId, {
        eventType: 'scrap_entry_deleted',
        description: 'Deleted a scrap entry',
        metadata: {
          entryId: String(id),
        },
      }).catch((error) => {
        console.warn('Failed to record scrap delete audit event:', error);
      });
      toast.success('Entry deleted');
      loadEntries();
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete');
    }
  };

  const handleExport = async (format: 'csv' | 'json') => {
    if (!permissions?.scrapManagement?.download) {
      toast.error('You do not have permission to download data');
      return;
    }
    try {
      const options: any = {};
      if (startDate) options.startDate = startDate.toISOString().split('T')[0];
      if (endDate) options.endDate = endDate.toISOString().split('T')[0];
      const blob = await adminApi.downloadScrapData(token, clientId, format, options);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const date = new Date().toISOString().split('T')[0];
      const range = startDate && endDate ? `_${startDate.toISOString().split('T')[0]}_to_${endDate.toISOString().split('T')[0]}` : `_${date}`;
      a.download = `scrap_data${range}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Scrap data downloaded as ${format.toUpperCase()}`);
    } catch {
      // Fallback: build CSV from loaded entries
      const headers = ['Date', 'Machine', 'Part Number', 'Quantity (kg)', 'Reason', 'Operator', 'Notes'];
      const rows = entries.map(e => [
        e.entry_date || e.entryDate || '',
        e.machine || '',
        e.part_number || e.partNumber || '',
        e.quantity || 0,
        e.reason || '',
        e.operator || '',
        e.description || e.notes || '',
      ]);
      const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `scrap_data_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('Exported from loaded data');
    }
  };

    const handleExportPDF = async () => {
      if (!permissions?.scrapManagement?.download) { toast.error('No download permission'); return; }
      try {
        const token_str = sessionStorage.getItem('authToken') || token;
        const res = await adminApi.getPDFTemplate(token_str, clientId);
        // always rebuild template and copy logos
        let template: any = {
          reportTitle: 'Scrap Management Report',
          reportSubtitle: 'Scrap Data for {clientName}',
          companyFields: [
            { label: 'Client', value: '{clientName}' },
            { label: 'Report Type', value: 'Scrap Management' },
          ],
          showDateRange: true,
          dateRangeLabel: 'Report Period',
          includeCharts: true, // Enable charts!
          sections: [
            {
              id: 'scrap',
              title: 'Scrap Records',
              columns: [
                { id: 'entry_num', dataKey: 'entry_num', label: 'Entry #', format: 'number' as const },
                { id: 'machine', dataKey: 'machine', label: 'Machine', format: 'text' as const },
                { id: 'part_number', dataKey: 'part_number', label: 'Part Number', format: 'text' as const },
                { id: 'quantity', dataKey: 'quantity', label: 'Quantity (kg)', format: 'number' as const },
                { id: 'reason', dataKey: 'reason', label: 'Reason', format: 'text' as const },
              ]
            }
          ],
          primaryColor: '#dc2626',
          borderStyle: 'single' as const,
          fontSize: 'medium' as const,
          showCheckedBy: false,
          showVerifiedBy: false,
          showGeneratedBy: true,
          footerText: `Generated on {date} {time}`,
        };
        if(res?.advancedTemplate){
          if(res.advancedTemplate.leftLogo) template.leftLogo = res.advancedTemplate.leftLogo;
          if(res.advancedTemplate.rightLogo) template.rightLogo = res.advancedTemplate.rightLogo;
        }

        // Build scrap data with numeric identifiers for charting
        const historicalData = entries.map((e: any, idx: number) => ({
          entry_num: idx + 1,
          machine: e.machine || '',
          part_number: e.part_number || e.partNumber || '',
          quantity: Number(e.quantity || 0),
          reason: e.reason || '',
          operator: e.operator || ''
        }));

        console.log('[Scrap PDF] Exporting with data:', historicalData.slice(0, 3));
        console.log('[Scrap PDF] Template includeCharts:', template.includeCharts);
        
        // Use correct call signature: liveData, historicalData, template, clientName, startDate?, endDate?
        const clientName = sessionStorage.getItem('clientName') || 'Scrap Management Client';
        await generateCustomPLCReport([], historicalData, template, clientName, startDate, endDate);
        await adminApi.logClientAuditEvent(token_str, clientId, {
          eventType: 'pdf_download',
          description: 'Downloaded scrap management PDF report',
          metadata: {
            report: 'scrap_management',
            fileName: `scrap_management_${new Date().toISOString().split('T')[0]}.pdf`,
            recordCount: historicalData.length,
            startDate: startDate ? startDate.toISOString() : null,
            endDate: endDate ? endDate.toISOString() : null,
          },
        }).catch((error) => {
          console.warn('Failed to record scrap PDF audit event:', error);
        });
        toast.success('PDF exported successfully');
      } catch (e: any) {
        console.error('PDF export error:', e);
        toast.error(e.message || 'PDF export failed');
      }
    };

  if (!permissions?.scrapManagement?.view) {
    return (
      <div className="flex items-center justify-center h-96">
        <Card className="p-8 text-center">
          <p className="text-muted-foreground">You do not have permission to view this page.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-orange-600 flex items-center justify-center shadow-lg shadow-red-500/25">
            <AlertTriangle className="h-5 w-5 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold tracking-tight">Scrap Management</h2>
            <p className="text-xs text-muted-foreground">{new Date().toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2 h-9 text-xs">
                <CalendarIcon className="h-3.5 w-3.5" />{startDate ? format(startDate, 'MMM dd') : 'From'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={startDate} onSelect={setStartDate} /></PopoverContent>
          </Popover>
          <span className="text-muted-foreground text-xs">→</span>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2 h-9 text-xs">
                <CalendarIcon className="h-3.5 w-3.5" />{endDate ? format(endDate, 'MMM dd') : 'To'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={endDate} onSelect={setEndDate} /></PopoverContent>
          </Popover>
          <Button variant="ghost" size="sm" onClick={loadEntries} className="h-9 w-9 p-0">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          {permissions?.scrapManagement?.download && (
            <>
              <Button size="sm" className="gap-2 h-9 text-xs bg-gradient-to-r from-red-500 to-orange-600 hover:from-red-600 hover:to-orange-700 shadow-md shadow-red-500/20" onClick={() => handleExport('csv')}>
                <Download className="h-3.5 w-3.5" />CSV
              </Button>
              <Button size="sm" onClick={handleExportPDF} className="gap-2 h-auto font-semibold bg-gradient-to-r from-red-500 to-orange-600 hover:from-red-600 hover:to-orange-700 shadow-md shadow-red-500/20 text-white">
                <Download className="h-3.5 w-3.5" />Export PDF
              </Button>
              <Button size="sm" variant="outline" className="gap-2 h-9 text-xs" onClick={() => handleExport('json')}>
                <Download className="h-3.5 w-3.5" />JSON
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Entries', value: String(entries.length), sub: 'Logged records', icon: '📋', gradient: 'from-blue-500 to-indigo-600', bg: 'from-blue-500/10 to-indigo-500/10', border: 'border-blue-500/20', text: 'text-blue-500' },
          { label: 'Total Scrap', value: `${totalScrap.toFixed(2)} kg`, sub: 'Accumulated waste', icon: '⚠️', gradient: 'from-red-500 to-rose-600', bg: 'from-red-500/10 to-rose-500/10', border: 'border-red-500/20', text: 'text-red-500' },
          { label: 'Rate / kg', value: `₹${ratePerKg.toFixed(2)}`, sub: 'Current scrap rate', icon: '💰', gradient: 'from-amber-500 to-orange-600', bg: 'from-amber-500/10 to-orange-500/10', border: 'border-amber-500/20', text: 'text-amber-500' },
          { label: 'Total Value', value: `₹${totalValue.toFixed(2)}`, sub: 'Estimated loss', icon: '📊', gradient: 'from-emerald-500 to-green-600', bg: 'from-emerald-500/10 to-green-500/10', border: 'border-emerald-500/20', text: 'text-emerald-500' },
        ].map((stat, i) => (
          <motion.div key={stat.label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07 }}>
            <Card className={`bg-gradient-to-br ${stat.bg} ${stat.border} border overflow-hidden relative`}>
              <div className={`absolute -top-4 -right-4 w-16 h-16 rounded-full bg-gradient-to-br ${stat.gradient} opacity-10`} />
              <CardContent className="p-5">
                <p className="text-2xl mb-2">{stat.icon}</p>
                <p className="text-[11px] text-muted-foreground uppercase tracking-wider mb-1">{stat.label}</p>
                <p className={`text-xl font-bold ${stat.text} tabular-nums`}>{stat.value}</p>
                <p className="text-[10px] text-muted-foreground mt-1">{stat.sub}</p>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Add Entry Form */}
        <Card className="lg:col-span-2 border-border/60">
          <CardHeader className="pb-4 border-b border-border/40">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <div className="w-6 h-6 rounded-md bg-gradient-to-br from-red-500 to-orange-600 flex items-center justify-center">
                <Plus className="h-3.5 w-3.5 text-white" />
              </div>
              Add Scrap Entry
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-5">
            <form onSubmit={handleAddEntry} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { id: 'machine', label: 'Machine / Line *', placeholder: 'e.g. LINE-01', key: 'machine' as const },
                  { id: 'part', label: 'Part Number', placeholder: 'e.g. PART-A001', key: 'partNumber' as const },
                  { id: 'scrapKg', label: 'Scrap Qty (kg) *', placeholder: '0.00', key: 'scrapKg' as const, type: 'number' },
                  { id: 'reason', label: 'Reason', placeholder: 'e.g. Defect, Overrun', key: 'reason' as const },
                  { id: 'operator', label: 'Operator', placeholder: 'Operator name', key: 'operator' as const },
                  { id: 'notes', label: 'Notes', placeholder: 'Additional notes', key: 'notes' as const },
                ].map(({ id, label, placeholder, key, type }) => (
                  <div key={id} className="space-y-1.5">
                    <Label htmlFor={id} className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</Label>
                    <Input id={id} type={type || 'text'} placeholder={placeholder} min={type === 'number' ? '0' : undefined} step={type === 'number' ? '0.01' : undefined}
                      value={formData[key]} onChange={(e) => setFormData({ ...formData, [key]: e.target.value })} />
                  </div>
                ))}
              </div>
              <Button type="submit"
                className="w-full h-11 gap-2 bg-gradient-to-r from-red-500 to-orange-600 hover:from-red-600 hover:to-orange-700 shadow-md shadow-red-500/20 font-semibold"
                disabled={saving || !permissions?.scrapManagement?.edit}>
                {saving ? <><RefreshCw className="h-4 w-4 animate-spin" />Saving...</> : <><Plus className="h-4 w-4" />Add Scrap Entry</>}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Rate Card */}
        <Card className="border-border/60 bg-gradient-to-br from-card to-muted/20">
          <CardHeader className="pb-4 border-b border-border/40">
            <CardTitle className="text-base font-semibold">💹 Scrap Rate Settings</CardTitle>
          </CardHeader>
          <CardContent className="pt-5 space-y-4">
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Rate per kg (₹)</Label>
              <Input type="number" min="0" step="0.01" placeholder="e.g. 45.00"
                value={ratePerKgInput} onChange={(e) => setRatePerKgInput(e.target.value)} />
              {permissions?.scrapManagement?.edit && (
                <Button size="sm" className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700" onClick={handleSaveRate}>
                  Save Rate
                </Button>
              )}
            </div>
            <div className="space-y-3 pt-3 border-t border-border/40">
              {[
                { label: 'Total Scrap', val: `${totalScrap.toFixed(2)} kg` },
                { label: 'Rate', val: `₹${ratePerKg.toFixed(2)}/kg` },
              ].map(({ label, val }) => (
                <div key={label} className="flex justify-between items-center p-2.5 rounded-lg bg-muted/40">
                  <span className="text-xs text-muted-foreground">{label}</span>
                  <span className="text-sm font-semibold tabular-nums">{val}</span>
                </div>
              ))}
              <div className="flex justify-between items-center p-3 rounded-xl bg-gradient-to-r from-emerald-500/10 to-green-500/10 border border-emerald-500/20">
                <span className="text-sm font-semibold">Total Value</span>
                <span className="text-lg font-bold text-emerald-500 tabular-nums">₹{totalValue.toFixed(2)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Entries Table */}
      <Card className="border-border/60">
        <CardHeader className="pb-4 border-b border-border/40">
          <CardTitle className="text-base font-semibold flex items-center justify-between">
            <span>Scrap Log {startDate && endDate ? `· ${format(startDate, 'MMM dd')} – ${format(endDate, 'MMM dd')}` : '· All Dates'}</span>
            <span className="text-sm font-normal px-2.5 py-1 rounded-full bg-muted text-muted-foreground">{entries.length} entries</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : entries.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <AlertTriangle className="h-10 w-10 mx-auto mb-3 opacity-20" />
              <p className="font-medium">No scrap entries found</p>
              <p className="text-xs mt-1">Add an entry using the form above</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/30 text-left text-muted-foreground text-[11px] uppercase tracking-widest">
                    {['Date', 'Machine', 'Part No.', 'Qty (kg)', 'Value (₹)', 'Reason', 'Operator'].map(h => (
                      <th key={h} className="py-3 px-3 sm:px-4 font-semibold">{h}</th>
                    ))}
                    {permissions?.scrapManagement?.edit && <th className="py-3 px-3 sm:px-4 text-right font-semibold">Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry, i) => {
                    const qty = Number(entry.quantity || 0);
                    const val = qty * ratePerKg;
                    return (
                      <motion.tr key={entry.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.02 }}
                        className="border-b last:border-0 hover:bg-muted/20 transition-colors group">
                        <td className="py-3 px-3 sm:px-4 font-mono text-xs text-muted-foreground">{entry.entry_date || entry.entryDate || '—'}</td>
                        <td className="py-3 px-3 sm:px-4 font-semibold">{entry.machine || '—'}</td>
                        <td className="py-3 px-3 sm:px-4">
                          <span className="px-2 py-0.5 rounded-md bg-muted text-xs font-mono">{entry.part_number || entry.partNumber || '—'}</span>
                        </td>
                        <td className="py-3 px-3 sm:px-4">
                          <span className="font-bold text-red-500 tabular-nums">{qty.toFixed(2)}</span>
                          <span className="text-[10px] text-muted-foreground ml-1">kg</span>
                        </td>
                        <td className="py-3 px-3 sm:px-4 font-semibold text-amber-500 tabular-nums">₹{val.toFixed(2)}</td>
                        <td className="py-3 px-3 sm:px-4 text-muted-foreground text-xs">{entry.reason || '—'}</td>
                        <td className="py-3 px-3 sm:px-4 text-muted-foreground">{entry.operator || '—'}</td>
                        {permissions?.scrapManagement?.edit && (
                          <td className="py-3 px-3 sm:px-4 text-right">
                            <Button size="icon" variant="ghost" onClick={() => handleDelete(entry.id)}
                              className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500/10">
                              <Trash2 className="h-3.5 w-3.5 text-red-500" />
                            </Button>
                          </td>
                        )}
                      </motion.tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
